import { createResponseSchema } from '@mint-sdk/api-core';
import { z } from 'zod';

export const UtmOptionsResponseEnum = z.enum([
  'utmSource',
  'utmMedium',
  'utmCampaign',
  'utmTerm',
  'utmContent',
]);

export const UtmOptionsEnum = z.enum([
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
]);

export const RowLevelEnum = z.enum([
  'activity',
  'platform',
  'media_row',
  'campaign',
  'ad_group',
  'ad',
]);
export const RowLevelDeprecatedEnum = z.enum([
  'channel',
  'platform',
  'media_row',
  'hierarchy_level_one',
  'hierarchy_level_two',
  'hierarchy_level_three',
  'hierarchy_level_four',
]);
export const GroupingLevelsEnum = z.enum([
  'platform',
  'media_row',
  'campaign',
  'ad_set',
  'io',
  'li',
  'ad_group',
  'ad',
  'pricing_item',
]);

export const GroupingLevelsCamelCaseEnum = z.enum([
  'platform',
  'mediaRow',
  'campaign',
  'adSet',
  'io',
  'li',
  'adGroup',
  'ad',
  'pricingItem',
]);

// Dynamic fields schema
export const DynamicFieldsItemSchema = z.partialRecord(
  GroupingLevelsCamelCaseEnum,
  z.array(
    z.object({
      key: z.string(),
      label: z.string(),
    }),
  ),
);
export const DynamicFieldsItemDeprecatedSchema = z.partialRecord(
  GroupingLevelsCamelCaseEnum,
  z.array(z.string()),
);

export const UtmOperationsEnum = z.enum({
  equals: 'Equals',
  contains: 'Contains',
});

export const GroupingSchema = z.object({
  id: z.string(),
  level: GroupingLevelsEnum,
  platform: z.string(),
  platformId: z.string(),
});

export const GroupingDeprecatedSchema = z.object({
  channel: z.string(),
  id: z.string(),
  level: GroupingLevelsEnum,
  platform: z.string(),
  utmConfigId: z.string(),
});

export const UtmItemDeprecatedSchema = z.object({
  equals: z.string().optional(),
  contains: z.array(z.string()).optional(),
  isDynamicField: z.boolean().nullish(),
});

export const UtmItemContainsSchema = z.object({
  value: z.string(),
  isDynamicField: z.boolean(),
});
export const UtmItemSchema = z.object({
  equals: z.string().optional(),
  contains: z
    .array(
      // TODO: remove .array(z.string())
      z.string(),
    )
    .or(z.array(UtmItemContainsSchema))
    .optional(),
  isDynamicField: z.boolean().nullish(),
});

export const SubRuleDeprecatedSchema = z.partialRecord(
  UtmOptionsResponseEnum,
  UtmItemDeprecatedSchema.nullable(),
);

export const SubRuleSchema = z.partialRecord(
  UtmOptionsResponseEnum,
  UtmItemSchema.nullable(),
);

export function createRuleSchema<T extends z.ZodTypeAny>(subRulesSchema: T) {
  return z.object({
    groupingId: z.string().nullable(),
    groupingLevel: GroupingLevelsEnum.nullable(),
    id: z.string(),
    isValidToSave: z.boolean().nullable(),
    parentPlatformItemId: z.string().nullable(),
    parentTileId: z.string().nullable(),
    platform: z.string().nullable(),
    platformId: z.string().nullable(),
    platformItemExternalId: z.string().nullable(),
    platformItemId: z.string().nullable(),
    subRules: subRulesSchema,
    tileId: z.string().nullable(),
    duplicatedSubRuleIndexes: z.array(z.number()).nullable(),
  });
}
export const RuleSchema = createRuleSchema(z.array(SubRuleSchema));
export function createRuleDeprecatedSchema<T extends z.ZodTypeAny>(
  subRulesSchema: T,
) {
  return z.object({
    channel: z.string().nullable(),
    groupingId: z.string().nullable(),
    groupingLevel: GroupingLevelsEnum.nullable(),
    id: z.string(),
    isValidToSave: z.boolean().nullable(),
    parentPlatformItemId: z.string().nullable(),
    parentTileId: z.string().nullable(),
    platform: z.string().nullable(),
    platformItemExternalId: z.string().nullable(),
    platformItemId: z.string().nullable(),
    subRules: subRulesSchema,
    tileId: z.string().nullable(),
  });
}
export const RuleDeprecatedSchema = createRuleDeprecatedSchema(
  z.array(SubRuleDeprecatedSchema),
);

export const GroupingLevelsLabelsSchema = z.partialRecord(
  GroupingLevelsCamelCaseEnum,
  z.string(),
);

export const ParentsSchema = z.array(
  z.object({
    levelId: z.string(),
    name: z.string(),
    label: z.string(),
  }),
);

const BaseAnalyticsTaxonomyItemSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  label: z.string().nullable(),
  hasSubItems: z.boolean(),
  rowLevel: RowLevelEnum,
  externalId: z.string().nullable(),
  userDefinedId: z.string().nullable().optional(),
  isAutotaggingSupported: z.boolean(),
});

const NullableGroupingFieldsSchema = z.object({
  grouping: GroupingSchema.nullable(),
  groupingLevel: GroupingLevelsEnum.nullable(),
  groupingLevelsLabels: GroupingLevelsLabelsSchema.nullable(),
  availableGroupingLevels: GroupingLevelsEnum.array().nullable(),
});

const NullableRuleFieldSchema = z.object({
  rule: RuleSchema.nullable(),
});

const NullableGroupingLevelSchema = z.object({
  groupingLevel: GroupingLevelsEnum.nullable(),
});

const NonNullableGroupingLevelSchema = z.object({
  groupingLevel: GroupingLevelsEnum,
});

const NonNullableGroupingFieldsSchema = z.object({
  grouping: GroupingSchema,
  groupingLevelsLabels: GroupingLevelsLabelsSchema,
  availableGroupingLevels: GroupingLevelsEnum.array(),
});

const NonNullableRuleFieldSchema = z.object({
  rule: RuleSchema,
});

const NonNullableParentsFieldSchema = z.object({
  parents: ParentsSchema,
});

const NullableParentsFieldSchema = z.object({
  parents: ParentsSchema.nullable(),
});

const DynamicFieldsSchema = z.object({
  dynamicFields: z.union([
    DynamicFieldsItemDeprecatedSchema,
    DynamicFieldsItemSchema,
  ]),
});

export const AnalyticsTaxonomyItemSchema = z.union([
  BaseAnalyticsTaxonomyItemSchema.extend({
    rowLevel: z.literal('activity'),
  })
    .and(NullableGroupingLevelSchema)
    .and(NullableGroupingFieldsSchema)
    .and(NullableRuleFieldSchema)
    .and(NullableParentsFieldSchema),
  BaseAnalyticsTaxonomyItemSchema.extend({
    rowLevel: z.literal('platform'),
  })
    .and(NonNullableGroupingLevelSchema)
    .and(NonNullableGroupingFieldsSchema)
    .and(NonNullableRuleFieldSchema)
    .and(NonNullableParentsFieldSchema)
    .and(DynamicFieldsSchema),
  BaseAnalyticsTaxonomyItemSchema.extend({
    rowLevel: z.literal('media_row'),
  })
    .and(NonNullableGroupingLevelSchema)
    .and(NullableGroupingFieldsSchema)
    .and(NonNullableRuleFieldSchema)
    .and(NonNullableParentsFieldSchema),
  BaseAnalyticsTaxonomyItemSchema.extend({
    rowLevel: RowLevelEnum.refine(
      (val) => val !== 'activity' && val !== 'media_row' && val !== 'platform',
    ),
  })
    .and(NonNullableGroupingLevelSchema)
    .and(NullableGroupingFieldsSchema)
    .and(NonNullableRuleFieldSchema)
    .and(NullableParentsFieldSchema),
]);

export const AnalyticsTaxonomyItemDeprecatedSchema = z.object({
  availableGroupingLevels: GroupingLevelsEnum.array().nullable(),
  externalId: z.string().nullable(),
  groupKey: z.string(),
  grouping: GroupingDeprecatedSchema.nullable(),
  groupingLevel: GroupingLevelsEnum.nullable(),
  groupingLevelsLabels: GroupingLevelsLabelsSchema.nullable(),
  hasSubItems: z.boolean(),
  id: z.string(),
  label: z.string(),
  name: z.string(),
  rowLevel: RowLevelDeprecatedEnum,
  rule: RuleDeprecatedSchema,
});

// Initial lvl request
export const UTMTableResponseSchema = createResponseSchema(
  AnalyticsTaxonomyItemSchema,
  z.object({
    groupingLevel: RowLevelEnum.nullable(),
  }),
);
export const UTMTableResponseDeprecatedSchema = createResponseSchema(
  AnalyticsTaxonomyItemDeprecatedSchema,
  z.object({
    groupingLevel: RowLevelDeprecatedEnum.nullable(),
  }),
);

export type UTMTableResponseType = z.infer<typeof UTMTableResponseSchema>;
export type UTMTableResponseDeprecatedType = z.infer<
  typeof UTMTableResponseDeprecatedSchema
>;

export type RowLevelType = z.infer<typeof RowLevelEnum>;
export type RowLevelDeprecatedType = z.infer<typeof RowLevelDeprecatedEnum>;
export type GroupingLevelsType = z.infer<typeof GroupingLevelsEnum>;
export type GroupingLevelsCamelCaseType = z.infer<
  typeof GroupingLevelsCamelCaseEnum
>;
export type DynamicFieldsItemType = z.infer<
  typeof DynamicFieldsItemDeprecatedSchema
>;
export type GroupingType = z.infer<typeof GroupingSchema>;
export type GroupingDeprecatedType = z.infer<typeof GroupingDeprecatedSchema>;
export type UtmOptionsResponseEnumType = z.infer<typeof UtmOptionsResponseEnum>;
export type UtmItemDeprecatedType = z.infer<typeof UtmItemDeprecatedSchema>;
export type UtmItemType = z.infer<typeof UtmItemSchema>;
export type UtmOptionsType = z.infer<typeof UtmOptionsEnum>;
export type SubRuleDeprecatedType = z.infer<typeof SubRuleDeprecatedSchema>;
export type SubRuleType = z.infer<typeof SubRuleSchema>;
export type RuleType = z.infer<typeof RuleSchema>;
export type GroupingLevelsLabelsType = z.infer<
  typeof GroupingLevelsLabelsSchema
>;
export type TaxonomyItemType = z.infer<typeof AnalyticsTaxonomyItemSchema>;
export type AnalyticsTaxonomyItemType = z.infer<
  typeof AnalyticsTaxonomyItemDeprecatedSchema
>;
export type UtmOperationsType = z.infer<typeof UtmOperationsEnum>;
export type UtmItemContainsType = z.infer<typeof UtmItemContainsSchema>;
